import React, { useMemo, useState } from "react";
import { ActionIcon, Box, Checkbox, Group, Paper, ScrollArea, Stack, Text } from "@mantine/core";
import type { IMcappsPnsNode } from "@multiconsult-group/multiplacer-core";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";

type TPnsNodeId = string;

type PnsTreePanelProps = {
  items: IMcappsPnsNode[];
  title?: string;
  selectedIds?: TPnsNodeId[];
  defaultSelectedIds?: TPnsNodeId[];
  onSelectedIdsChange?: (ids: TPnsNodeId[]) => void;
  expandedIds?: TPnsNodeId[];
  defaultExpandedIds?: TPnsNodeId[];
  onExpandedIdsChange?: (ids: TPnsNodeId[]) => void;
};

type TTreeNode = IMcappsPnsNode & {
  children: TTreeNode[];
};

export const PnsTreePanel: React.FC<PnsTreePanelProps> = ({
  items,
  title = "PNS",
  selectedIds,
  defaultSelectedIds = [],
  onSelectedIdsChange,
  expandedIds,
  defaultExpandedIds = [],
  onExpandedIdsChange,
}) => {
  const [internalSelectedIds, setInternalSelectedIds] = useState<TPnsNodeId[]>(defaultSelectedIds);
  const [internalExpandedIds, setInternalExpandedIds] = useState<TPnsNodeId[]>(defaultExpandedIds);
  const isControlled = selectedIds != null;
  const isExpandedControlled = expandedIds != null;
  const activeSelectedIds = isControlled ? selectedIds : internalSelectedIds;
  const activeExpandedIds = isExpandedControlled ? expandedIds : internalExpandedIds;
  const selectedSet = useMemo(() => new Set(activeSelectedIds), [activeSelectedIds]);
  const expandedSet = useMemo(() => new Set(activeExpandedIds), [activeExpandedIds]);

  const roots = useMemo(() => buildTree(items), [items]);

  const setSelected = (next: TPnsNodeId[]) => {
    if (!isControlled) {
      setInternalSelectedIds(next);
    }
    onSelectedIdsChange?.(next);
  };

  const setExpanded = (next: TPnsNodeId[]) => {
    if (!isExpandedControlled) {
      setInternalExpandedIds(next);
    }
    onExpandedIdsChange?.(next);
  };

  const handleToggle = (node: TTreeNode, checked: boolean) => {
    const next = new Set(activeSelectedIds);
    const nodeAndDescendantPnsIds = collectNodeAndDescendantPnsIds(node);

    if (checked) {
      for (const id of nodeAndDescendantPnsIds) {
        next.add(id);
      }
    } else {
      for (const id of nodeAndDescendantPnsIds) {
        next.delete(id);
      }
    }

    setSelected(Array.from(next));
  };

  const handleToggleExpanded = (nodeId: TPnsNodeId) => {
    const next = new Set(activeExpandedIds);
    if (next.has(nodeId)) {
      next.delete(nodeId);
    } else {
      next.add(nodeId);
    }
    setExpanded(Array.from(next));
  };

  return (
    <Paper
      withBorder
      radius="md"
      h="100%"
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Group px="sm" py="xs" justify="space-between" style={{ borderBottom: "1px solid var(--mantine-color-gray-3)" }}>
        <Text fw={700} size="sm">
          {title}
        </Text>
        <Text size="xs" c="dimmed">
          {activeSelectedIds.length} valgt
        </Text>
      </Group>

      <ScrollArea type="auto" scrollbarSize={8} style={{ flex: 1, minHeight: 0 }}>
        <Stack gap={2} p="xs">
          {roots.length === 0 ? (
            <Text size="sm" c="dimmed">
              Ingen PNS-data
            </Text>
          ) : (
            roots.map((node) => (
              <PnsTreeRow
                key={node._id}
                node={node}
                level={0}
                selectedSet={selectedSet}
                expandedSet={expandedSet}
                onToggle={handleToggle}
                onToggleExpanded={handleToggleExpanded}
              />
            ))
          )}
        </Stack>
      </ScrollArea>
    </Paper>
  );
};

type PnsTreeRowProps = {
  node: TTreeNode;
  level: number;
  selectedSet: Set<TPnsNodeId>;
  expandedSet: Set<TPnsNodeId>;
  onToggle: (node: TTreeNode, checked: boolean) => void;
  onToggleExpanded: (nodeId: TPnsNodeId) => void;
};

const PnsTreeRow: React.FC<PnsTreeRowProps> = ({
  node,
  level,
  selectedSet,
  expandedSet,
  onToggle,
  onToggleExpanded,
}) => {
  const checked = selectedSet.has(node.props.pnsId);
  const hasChildren = node.children.length > 0;
  const isExpanded = hasChildren ? expandedSet.has(node._id) : false;

  return (
    <Box>
      <Group gap={4} wrap="nowrap" align="center" style={{ paddingLeft: level * 16 }}>
        {hasChildren ? (
          <ActionIcon
            size="sm"
            variant="subtle"
            color="gray"
            style={{ alignSelf: "center", marginTop: 1 }}
            onClick={() => onToggleExpanded(node._id)}
            aria-label={isExpanded ? "Collapse children" : "Expand children"}
          >
            {isExpanded ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
          </ActionIcon>
        ) : (
          <Box w={26} />
        )}

        <Checkbox
          checked={checked}
          onChange={(event) => onToggle(node, event.currentTarget.checked)}
          label={`${node.props.pnsId} ${node.props.title}`}
          styles={{
            root: { flex: 1, minWidth: 0 },
            body: { alignItems: "flex-start" },
            labelWrapper: { lineHeight: 1.2 },
            label: { fontSize: 13, whiteSpace: "normal" },
          }}
        />
      </Group>

      {hasChildren && isExpanded && (
        <Stack gap={2} mt={2}>
          {node.children.map((child) => (
            <PnsTreeRow
              key={child._id}
              node={child}
              level={level + 1}
              selectedSet={selectedSet}
              expandedSet={expandedSet}
              onToggle={onToggle}
              onToggleExpanded={onToggleExpanded}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

function buildTree(items: IMcappsPnsNode[]): TTreeNode[] {
  const byId = new Map<string, TTreeNode>();

  for (const item of items) {
    byId.set(item._id, {
      ...item,
      children: [],
    });
  }

  const roots: TTreeNode[] = [];

  for (const item of items) {
    const node = byId.get(item._id);
    if (!node) continue;

    if (item.parentId && byId.has(item.parentId)) {
      byId.get(item.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const sortNodes = (nodes: TTreeNode[]) => {
    nodes.sort((a, b) => a.props.pnsId.localeCompare(b.props.pnsId, undefined, { numeric: true }));
    nodes.forEach((n) => sortNodes(n.children));
  };

  sortNodes(roots);

  return roots;
}

function collectNodeAndDescendantPnsIds(node: TTreeNode): TPnsNodeId[] {
  const ids: TPnsNodeId[] = [node.props.pnsId];

  for (const child of node.children) {
    ids.push(...collectNodeAndDescendantPnsIds(child));
  }

  return ids;
}

export default PnsTreePanel;
